import React from 'react';
import { FormattedMessage } from 'react-intl';

import { Field } from '@atlaskit/form';
import { RadioGroup } from '@atlaskit/radio';
import { OptionsPropType } from '@atlaskit/radio/types';

import { messages } from '../messages';

export type SiteSelectorRadioGroupProps = {
  readonly sites: string[];

  readonly onSiteChanged: () => void;
};

export type SiteSelectorRadioGroupState = {
  fieldName: string;
  radioOptions: OptionsPropType;
  selectedSite: string | number | undefined;
};

export class SiteSelectorRadioGroup extends React.Component<
  SiteSelectorRadioGroupProps,
  SiteSelectorRadioGroupState
> {
  constructor(props: SiteSelectorRadioGroupProps) {
    super(props);

    const { sites } = this.props;

    const fieldName = 'site';
    const radioOptions = this.getRadioOptions(sites, fieldName);
    const defaultValue = radioOptions[0].value;

    this.state = {
      fieldName,
      radioOptions,
      selectedSite: defaultValue!
    };
  }

  getRadioOptions = (sites: string[], fieldName: string) => {
    const radioOptions: OptionsPropType = sites.map((site) => {
      // HOT-101715 fixes
      const { hostname: originalHostname, searchParams } = new URL(site);
      let hostname = originalHostname;
      try {
        const continueVal = searchParams.get('continue');
        hostname = continueVal
          ? new URL(continueVal).hostname
          : originalHostname;
      } catch {}
      return {
        name: fieldName,
        value: site,
        label: hostname
      };
    });

    // Include option to "Start a new site"
    radioOptions.push({
      name: fieldName,
      value: '',
      label: <FormattedMessage {...messages.startASite} />
    });

    return radioOptions;
  };

  onValueChange: React.ChangeEventHandler<HTMLInputElement> = (
    event: React.ChangeEvent
  ) => {
    this.props.onSiteChanged();

    // In our custom onChange handler, we need to manage the RadioGroup state in our component's state
    const newValue = event.currentTarget.getAttribute('value');
    const newSiteSelected = newValue ? newValue : undefined;

    this.setState({ selectedSite: newSiteSelected });
  };

  render() {
    const { fieldName, radioOptions, selectedSite } = this.state;

    return (
      <Field
        name={fieldName}
        label={<FormattedMessage {...messages.selectASite} />}
        defaultValue={selectedSite}
      >
        {({ fieldProps }: { fieldProps: object }) => (
          <RadioGroup
            {...fieldProps}
            options={radioOptions}
            onChange={this.onValueChange}
          />
        )}
      </Field>
    );
  }
}
